import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { delay, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { IPagedAndSortedRequest } from 'src/app/common/interfaces/paged-and-sorted-request';
import { AbsenceService } from 'src/app/data/absence.service';
import { formatDayCountFromTicks, formatDurationFromTicks } from 'src/app/common/utils/formatters/duration-format';
import { AbsenceDto } from 'src/app/models/dtos/absence-dto';
import { ExternalSyncStatusEnum } from 'src/app/models/enums/external-sync-status-enum';

interface PagingRequest extends IPagedAndSortedRequest {
  WorkerId: number;
  WorkerAgreementId: number | null;
  AbsenceTypeIds: number[];
  AbsenceStatusIds: number[];
  Year: number | null;
}

@Injectable()
export class WorkerAbsencesListDataSource {
  private absenceService = inject(AbsenceService);
  private translateService = inject(TranslateService);
  private localeId = inject(LOCALE_ID);

  countSubject = new BehaviorSubject<number>(0);
  count$: Observable<number> = this.countSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$: Observable<boolean> = this.loadingSubject.asObservable();

  absencesSubject = new Subject<PagingRequest>();

  absences$: Observable<AbsenceDto[]> = this.absencesSubject.pipe(
    delay(0),
    tap(() => this.loadingSubject.next(true)),
    switchMap((request) =>
      request.WorkerId
        ? this.absenceService
            .getAbsenceByWorkerId(
              request.WorkerId,
              request.WorkerAgreementId,
              request.Page,
              request.PageSize,
              request.SortingField,
              request.SortingDirection,
              request.AbsenceTypeIds,
              request.AbsenceStatusIds,
              request.Year,
            )
            .pipe(
              tap((res) => this.countSubject.next(res.Count)),
              map((res) => (res.Results ?? []).map((row) => enrichAbsenceRow(row, this.getFormattingLocale()))),
              finalize(() => this.loadingSubject.next(false)),
            )
        : of([]).pipe(
            tap(() => this.countSubject.next(0)),
            finalize(() => this.loadingSubject.next(false)),
          ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private getFormattingLocale(): string {
    return this.translateService.getCurrentLang() || this.translateService.getFallbackLang() || this.localeId;
  }
}

function enrichAbsenceRow(row: AbsenceDto, locale: string): AbsenceDto {
  if (row.IsScaledByWorkingTime) {
    return enrichScaledAbsenceRow(row, locale);
  }

  const isPartTime =
    row.WorkingTimeNominator != null &&
    row.WorkingTimeDenominator != null &&
    row.WorkingTimeNominator > 0 &&
    row.WorkingTimeDenominator > 0 &&
    !(row.WorkingTimeNominator === 1 && row.WorkingTimeDenominator === 1);

  const requestedDays = isPartTime
    ? row.BusinessTimeTicks * row.WorkingTimeDenominator! / row.WorkingTimeNominator!
    : null;

  return {
    ...row,
    RequestedDays: requestedDays,
    BusinessDaysDisplay: formatDurationFromTicks(row.BusinessTimeTicks, locale),
    RequestedDaysDisplay: requestedDays == null ? '' : formatDayCountFromTicks(requestedDays, locale),
  };
}

// Agreements with absence limit scaling: the backend sets N/D from the working time period of the first absence day
// and flags requests not fitting in that period.
// Before TETA processing ticks hold the requested working days; afterwards they hold the value from TETA (8h days).
function enrichScaledAbsenceRow(row: AbsenceDto, locale: string): AbsenceDto {
  const nominator = row.WorkingTimeNominator;
  const denominator = row.WorkingTimeDenominator;
  const isPartTime = !!nominator && !!denominator && nominator > 0 && denominator > 0 && nominator !== denominator;
  const isProcessed = row.ExternalSyncStatusId === ExternalSyncStatusEnum.ProcessedByExternalSystem;

  const requestedDays = !isPartTime
    ? null
    : isProcessed
      ? row.BusinessTimeTicks * denominator! / nominator!
      : row.BusinessTimeTicks;
  const businessTicks = isPartTime && !isProcessed
    ? row.BusinessTimeTicks * nominator! / denominator!
    : row.BusinessTimeTicks;

  return {
    ...row,
    RequestedDays: requestedDays,
    BusinessDaysDisplay: formatDurationFromTicks(businessTicks, locale),
    RequestedDaysDisplay: requestedDays == null ? '' : formatDayCountFromTicks(requestedDays, locale),
  };
}
